import { computed, ref, type ComputedRef, type Ref } from 'vue';

import { http } from '@/interceptors/axiosRequestInterceptor';

/**
 * The Needs Attention queue (AB#21587).
 *
 * The proactive counterpart to Ask ELSA: instead of opening an order and asking
 * how it's doing, this is the ranked list of the orders that need the person
 * today, each with the one or two actions that address it. The queue is
 * computed and scoped entirely server-side (NeedsAttentionService), so this
 * layer only fetches, holds and mutates — it makes no ranking or access
 * decisions of its own.
 *
 * Every response uses the app envelope { success, message, result }. A failed
 * load leaves the last good queue in place and records an error rather than
 * blanking the screen, because a queue that vanishes on a hiccup is worse than
 * a slightly stale one.
 */

export type AttentionBand = 'critical' | 'high' | 'medium';

export type AttentionAction =
  | 'assign_factory'
  | 'reply_factory'
  | 'set_ship_date'
  | 'get_tracking'
  | 'update_customer'
  | 'fix_order_type'
  | 'open_elsa';

export interface AttentionCta {
  label: string;
  action: AttentionAction | string;
}

export interface AttentionItem {
  order_item_id: number;
  order_id: number;
  order_number: string;
  company_name: string | null;
  customer_name: string | null;
  problem: string;
  severity: number;
  band: AttentionBand;
  headline: string;
  detail: string | null;
  cta: AttentionCta;
  secondary: AttentionCta | null;
  /** Signed: positive = days remaining, 0 = today, negative = days overdue, null = no date. */
  deadline_days: number | null;
  analysis_status: 'green' | 'orange' | 'red' | null;
  is_stale: boolean;
  order_administrator_id: number | null;
  /** The order's production status, for the stage indicator. Absent on older backends. */
  order_status?: string | null;
}

export interface TeamAdminLoad {
  admin_id: number;
  admin_name: string;
  open_count: number;
  critical_count: number;
}

export interface TeamRollup {
  per_admin: TeamAdminLoad[];
  unassigned: { count: number; items: AttentionItem[] };
  top_risks: AttentionItem[];
  total: number;
}

interface QueuePayload {
  items?: AttentionItem[];
  count?: number;
}

export interface UseNeedsAttention {
  items: Ref<AttentionItem[]>;
  count: Ref<number>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  hasItems: ComputedRef<boolean>;
  load: () => Promise<void>;
  refreshCount: () => Promise<void>;
  snooze: (orderItemId: number, snoozedUntil: string, reason?: string) => Promise<boolean>;
  dismiss: (orderItemId: number, reason?: string) => Promise<boolean>;
  restore: (orderItemId: number) => Promise<boolean>;
  loadTeam: () => Promise<TeamRollup | null>;
}

export const useNeedsAttention = (): UseNeedsAttention => {
  const items = ref<AttentionItem[]>([]);
  const count = ref(0);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const hasItems = computed(() => items.value.length > 0);

  const load = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { data } = await http.get('needs-attention');
      const result = (data?.result ?? {}) as QueuePayload;

      if (data?.success === true) {
        items.value = Array.isArray(result.items) ? result.items : [];
        count.value = typeof result.count === 'number' ? result.count : items.value.length;
      } else {
        // Keep the last good queue; just surface the message.
        error.value = data?.message || 'The queue could not be loaded.';
      }
    } catch {
      error.value = 'The queue could not be loaded.';
    } finally {
      isLoading.value = false;
    }
  };

  /** Just the badge number — cheap enough to poll. Never throws to the caller. */
  const refreshCount = async (): Promise<void> => {
    try {
      const { data } = await http.get('needs-attention/count');
      const result = (data?.result ?? {}) as { count?: number };
      if (typeof result.count === 'number') {
        count.value = result.count;
      }
    } catch {
      // A badge that can't refresh keeps its last number rather than clearing.
    }
  };

  /**
   * After a set-aside/restore, drop or keep the row locally so the list reacts
   * immediately, and reconcile the badge with the server. Optimistic on the
   * list, authoritative on the count.
   */
  const removeLocally = (orderItemId: number): void => {
    items.value = items.value.filter((i) => i.order_item_id !== orderItemId);
    count.value = items.value.length;
  };

  const snooze = async (
    orderItemId: number,
    snoozedUntil: string,
    reason?: string
  ): Promise<boolean> => {
    try {
      await http.post(`needs-attention/${orderItemId}/snooze`, {
        snoozed_until: snoozedUntil,
        reason: reason ?? null,
      });
      removeLocally(orderItemId);
      void refreshCount();
      return true;
    } catch {
      error.value = 'Could not snooze that item.';
      return false;
    }
  };

  const dismiss = async (orderItemId: number, reason?: string): Promise<boolean> => {
    try {
      await http.post(`needs-attention/${orderItemId}/dismiss`, { reason: reason ?? null });
      removeLocally(orderItemId);
      void refreshCount();
      return true;
    } catch {
      error.value = 'Could not dismiss that item.';
      return false;
    }
  };

  const restore = async (orderItemId: number): Promise<boolean> => {
    try {
      await http.post(`needs-attention/${orderItemId}/restore`);
      // A restore is best reflected by reloading — the item's rank decides where
      // it lands, which the client cannot know.
      await load();
      return true;
    } catch {
      error.value = 'Could not restore that item.';
      return false;
    }
  };

  const loadTeam = async (): Promise<TeamRollup | null> => {
    try {
      const { data } = await http.get('needs-attention/team');
      if (data?.success !== true) {
        error.value = data?.message || 'The team overview could not be loaded.';
        return null;
      }
      const r = (data?.result ?? {}) as Partial<TeamRollup>;
      return {
        per_admin: Array.isArray(r.per_admin) ? r.per_admin : [],
        unassigned: {
          count: r.unassigned?.count ?? 0,
          items: Array.isArray(r.unassigned?.items) ? (r.unassigned?.items as AttentionItem[]) : [],
        },
        top_risks: Array.isArray(r.top_risks) ? r.top_risks : [],
        total: typeof r.total === 'number' ? r.total : 0,
      };
    } catch {
      error.value = 'The team overview could not be loaded.';
      return null;
    }
  };

  return {
    items,
    count,
    isLoading,
    error,
    hasItems,
    load,
    refreshCount,
    snooze,
    dismiss,
    restore,
    loadTeam,
  };
};
